import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { NavigationBar } from '../components/NavigationBar';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { collectionsService } from '../services/collectionsService';
import { flashcardsService } from '../services/flashcardsService';
import { AIQuizGenerator } from '../components/AIQuizGenerator';

export const QuizCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector((state: RootState) => state.auth.user);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    flashcards: [{
      question: '',
      options: ['', '', '', ''],
      correctAnswer: 0
    }]
  });
  const [quizType, setQuizType] = useState<'manual' | 'ai' | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Set quiz type based on URL parameter
    const type = searchParams.get('type');
    if (type === 'manual' || type === 'ai') {
      setQuizType(type);
    }
  }, [user, navigate, searchParams]);

  const handleAddFlashcard = () => {
    if (!user?.isPremium && formData.flashcards.length >= 10) {
      setShowUpgradeModal(true);
      return;
    }
    setFormData(prev => ({
      ...prev,
      flashcards: [...prev.flashcards, {
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0
      }]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      // Create collection
      const collection = await collectionsService.createCollection({
        title: formData.title,
        description: formData.description
      });

      // Add flashcards
      for (const flashcard of formData.flashcards) {
        if (flashcard.question && flashcard.options[0]) {
          await flashcardsService.createFlashcard(collection._id, {
            question: flashcard.question,
            options: flashcard.options,
            correctAnswer: flashcard.correctAnswer
          });
        }
      }

      navigate('/quizzes');
    } catch (error: any) {
      if (error.response?.data?.message) {
        setError(error.response.data.message);
        if (error.response.data.message.includes('upgrade to premium')) {
          setShowUpgradeModal(true);
        }
      } else {
        setError('An error occurred while creating the quiz');
      }
    }
  };

  const renderContent = () => {
    if (!quizType) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto p-6"
        >
          <h2 className="text-2xl font-bold text-center mb-8 text-gray-900 dark:text-white">Choose Quiz Creation Method</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setQuizType('manual')}
              className="p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition-shadow border border-gray-100/50 dark:border-gray-700/50"
            >
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Manual Creation</h3>
              <p className="text-gray-600 dark:text-gray-300">Create your own quiz by writing questions and answers manually.</p>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setQuizType('ai')}
              className="p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-lg cursor-pointer hover:shadow-xl transition-shadow border border-gray-100/50 dark:border-gray-700/50"
            >
              <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">AI Generation</h3>
              <p className="text-gray-600 dark:text-gray-300">Let AI generate a quiz based on your chosen topic and difficulty level.</p>
            </motion.div>
          </div>
        </motion.div>
      );
    }

    return quizType === 'ai' ? (
      <AIQuizGenerator />
    ) : (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <button
          onClick={() => setQuizType(null)}
          className="mb-6 text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          ← Back to quiz types
        </button>
        
        <div className="bg-white/80 dark:bg-gray-800/90 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-100/50 dark:border-gray-700/50">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
            Create New Quiz
          </h1>

          {error && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400">
                Title
              </label>
              <div className="relative">
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="block w-full px-4 py-3 rounded-lg border-2 bg-white/50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-600 
                    focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800 dark:text-white 
                    placeholder-gray-400 transition-all duration-200 ease-in-out
                    hover:border-gray-300 dark:hover:border-gray-500"
                  placeholder="Enter quiz title"
                />
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 opacity-0 group-focus-within:opacity-10 transition-opacity pointer-events-none"></div>
              </div>
            </div>

            <div className="group">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400">
                Description
              </label>
              <div className="relative">
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="block w-full px-4 py-3 rounded-lg border-2 bg-white/50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-600 
                    focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800 dark:text-white 
                    placeholder-gray-400 transition-all duration-200 ease-in-out resize-none
                    hover:border-gray-300 dark:hover:border-gray-500"
                  placeholder="Describe what this quiz is about"
                />
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 opacity-0 group-focus-within:opacity-10 transition-opacity pointer-events-none"></div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Flashcards</h2>
                {!user?.isPremium && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formData.flashcards.length}/10 cards
                  </p>
                )}
              </div>

              {formData.flashcards.map((flashcard, index) => (
                <motion.div 
                  key={index} 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-lg p-6 space-y-4 border border-gray-200/50 dark:border-gray-700/50 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="group">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors group-focus-within:text-indigo-600 dark:group-focus-within:text-indigo-400">
                      Question {index + 1}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        required
                        value={flashcard.question}
                        onChange={e => {
                          const newFlashcards = [...formData.flashcards];
                          newFlashcards[index].question = e.target.value;
                          setFormData(prev => ({ ...prev, flashcards: newFlashcards }));
                        }}
                        className="block w-full px-4 py-3 rounded-lg border-2 bg-white/50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-600 
                          focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800 dark:text-white 
                          placeholder-gray-400 transition-all duration-200 ease-in-out
                          hover:border-gray-300 dark:hover:border-gray-500"
                        placeholder="Enter your question"
                      />
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 opacity-0 group-focus-within:opacity-10 transition-opacity pointer-events-none"></div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Options (select the correct answer)
                    </label>
                    {flashcard.options.map((option, optionIndex) => (
                      <div key={optionIndex} className="group flex items-center space-x-3">
                        <input
                          type="radio"
                          name={`correct-${index}`}
                          checked={flashcard.correctAnswer === optionIndex}
                          onChange={() => {
                            const newFlashcards = [...formData.flashcards];
                            newFlashcards[index].correctAnswer = optionIndex;
                            setFormData(prev => ({ ...prev, flashcards: newFlashcards }));
                          }}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 transition-colors"
                        />
                        <div className="relative flex-1">
                          <input
                            type="text"
                            required
                            value={option}
                            onChange={e => {
                              const newFlashcards = [...formData.flashcards];
                              newFlashcards[index].options[optionIndex] = e.target.value;
                              setFormData(prev => ({ ...prev, flashcards: newFlashcards }));
                            }}
                            className="block w-full px-4 py-2 rounded-lg border-2 bg-white/50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-600 
                              focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-800 dark:text-white 
                              placeholder-gray-400 transition-all duration-200 ease-in-out
                              hover:border-gray-300 dark:hover:border-gray-500"
                            placeholder={`Option ${optionIndex + 1}`}
                          />
                          <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-indigo-100 to-purple-100 dark:from-indigo-900/20 dark:to-purple-900/20 opacity-0 group-focus-within:opacity-10 transition-opacity pointer-events-none"></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {formData.flashcards.length > 1 && (
                    <motion.button
                      type="button"
                      onClick={() => {
                        const newFlashcards = formData.flashcards.filter((_, i) => i !== index);
                        setFormData(prev => ({ ...prev, flashcards: newFlashcards }));
                      }}
                      className="text-sm text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Remove Card
                    </motion.button>
                  )}
                </motion.div>
              ))}

              <button
                type="button"
                onClick={handleAddFlashcard}
                className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!user?.isPremium && formData.flashcards.length >= 10}
              >
                Add Flashcard
              </button>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/quizzes')}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-500 dark:hover:text-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Create Quiz
              </button>
            </div>
          </form>
        </div>

        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 max-w-md w-full shadow-xl"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Upgrade to Premium
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Free users can only create up to 10 flashcards per collection. Upgrade to Premium to create unlimited flashcards and unlock more features!
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                >
                  Maybe Later
                </button>
                <button
                  onClick={() => navigate('/pricing')}
                  className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-700 dark:from-indigo-600 dark:to-purple-700 dark:hover:from-indigo-700 dark:hover:to-purple-800"
                >
                  View Plans
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <NavigationBar />
      <div className="pt-20 px-4 pb-12">
        {renderContent()}
      </div>
    </div>
  );
};